/**
 * ConversationRow — one conversation of the list (UI-SPEC §2.19), and its paused variant
 * (« Désactivés » / « Mis en pause (offre) », §2.4b) with « Réactiver ».
 *
 * « Réactiver » previews first (PATCH `status: 'active', preview: true`, or POST `reactivate`
 * `{ preview: true }` for a plan_limit pause): self-serve `refused` → greyed with its reason, no
 * save; agency non-empty `warnings` → B's QuotaWarningDialog, then the save. The save's 409
 * ALLOWANCE_EXCEEDED greys it with the body's `figuresAfter`; the 429 persona gauge gets its own
 * line; nothing changes on screen until the server answers.
 */
import { useState } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { ConversationLoad, ConversationPersona, SaveWarnings } from '@traaker/shared';
import { AXIS_COLOR } from '../promptStudioModel';
import { limitReason, slotText } from '../promptStudioModel';
import { LocChip } from '../PromptRow';
import { PausedRowShell } from '../PausedRow';
import { QuotaWarningDialog } from '../QuotaWarningDialog';
import { Flag } from '../../../pages/dashboard/Flag';
import { languageFlag } from '../../../pages/dashboard/geoFlags';
import { languageName } from '../variantModel';
import { intentAxis, saveErrorKey, saveErrorOf } from './conversationModel';
import { conversationsApi, isAgencyCheck, savedPersona } from './conversationsApi';

export interface ConversationRowProps {
  persona: ConversationPersona;
  /** The live Brand Knowledge persona name (else the stored name). */
  title: string;
  intentLabel: string;
  open: boolean;
  onToggle: () => void;
  /** The opened row's form, rendered under the row. */
  children?: React.ReactNode;
}

/** Distinct places / languages of the row's cells, in stored order. */
function rowShape(p: ConversationPersona) {
  const places = new Map<string, ConversationPersona['cells'][number]['zoneMeta']>();
  const languages: string[] = [];
  const engines = new Set<string>();
  let runs = 0;
  for (const c of p.cells ?? []) {
    const k = c.zoneMeta ? `${c.zoneMeta.countryCode}|${c.zoneMeta.type}|${c.zoneMeta.label}` : '';
    if (!places.has(k)) places.set(k, c.zoneMeta);
    if (!languages.includes(c.language)) languages.push(c.language);
    engines.add(c.engine);
    runs += c.runs;
  }
  return { places: [...places.values()], languages, engines: engines.size, runs };
}

export function ConversationRow({ persona, title, intentLabel, open, onToggle, children }: ConversationRowProps) {
  const { t, i18n } = useTranslation();
  const shape = rowShape(persona);
  const variants = shape.languages.length * shape.places.length;
  const runsEach = shape.engines * variants > 0 ? Math.round(shape.runs / (shape.engines * variants)) : 0;
  const rowTitle = t('promptStudio.conv.rowTitle', {
    variants,
    engines: t('promptStudio.save.engines', { count: shape.engines }),
    runs: t('promptStudio.save.runs', { count: runsEach }),
  });

  return (
    <Box sx={{ borderBottom: '1px solid', borderColor: 'rgba(0,0,0,.06)', bgcolor: open ? '#fcfbff' : 'transparent', flexShrink: 0 }}>
      <Box
        onClick={onToggle}
        data-conv-row={persona.personaId}
        sx={{ display: 'flex', alignItems: 'center', minHeight: 46, cursor: 'pointer', '&:hover': { bgcolor: 'rgba(0,0,0,.02)' } }}
      >
        {/* Rail dot: the colour of the intent's axis (UI-SPEC §2.19) */}
        <Box sx={{ width: 6, minWidth: 6, alignSelf: 'stretch', bgcolor: AXIS_COLOR[intentAxis(persona.intentId)], flexShrink: 0 }} />

        <Box sx={{ flex: 1, minWidth: 0, px: 1.5, display: 'flex', alignItems: 'baseline', gap: 1, overflow: 'hidden' }}>
          <Box sx={{ fontSize: 14, fontWeight: 500, maxWidth: '62%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {title}
          </Box>
          <Box sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {intentLabel}
          </Box>
        </Box>

        {/* Lieu: first place + N */}
        <Box sx={{ width: 168, minWidth: 168, flexShrink: 0, px: 0.5 }}>
          <LocChip zone={shape.places[0] ?? null} />
          {shape.places.length > 1 && (
            <Box component="span" sx={{ fontSize: 12, color: 'text.disabled', ml: 0.25 }}>
              +{shape.places.length - 1}
            </Box>
          )}
        </Box>

        {/* Langues */}
        <Box
          title={shape.languages.map((c) => languageName(c, i18n.language)).join(', ') || undefined}
          sx={{ width: 68, minWidth: 68, flexShrink: 0, px: 0.5, display: 'flex', alignItems: 'center', gap: 0.25, fontSize: 12, color: 'text.secondary' }}
        >
          {shape.languages.map((code) =>
            languageFlag(code)
              ? <Flag key={code} countryCode={languageFlag(code)} size={18} title={languageName(code, i18n.language)} />
              : <span key={code}>{code.toUpperCase()}</span>,
          )}
        </Box>

        {/* Runs per launch */}
        <Box title={rowTitle} sx={{ width: 60, minWidth: 60, textAlign: 'right', fontSize: 12, color: 'text.secondary', pr: 0.5, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
          {shape.runs}
        </Box>
        <Box sx={{ width: 18, minWidth: 18, flexShrink: 0 }} />
      </Box>
      {open && children}
    </Box>
  );
}

export interface ConversationPausedRowProps {
  persona: ConversationPersona;
  title: string;
  /** 'plan' = « Mis en pause (offre) » (POST reactivate). 'user' = « Désactivés » (PATCH status). */
  variant: 'plan' | 'user';
  brandId: string;
  isAgency: boolean;
  onReactivated: (persona: ConversationPersona, load: ConversationLoad) => void;
  /** A network failure or a conflict: the list is reloaded before anything else is tried. */
  onReload: () => void;
}

export function ConversationPausedRow({ persona, title, variant, brandId, isAgency, onReactivated, onReload }: ConversationPausedRowProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [limited, setLimited] = useState<string | null>(null);
  const [line, setLine] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<SaveWarnings[] | null>(null);

  const fail = (err: unknown) => {
    const e = saveErrorOf(err);
    if (e.kind === 'refused') {
      const slot = limitReason(e.figuresAfter, 'conversationRuns');
      setLimited(t('promptStudio.paused.refused', { reason: slot ? slotText(t, slot) : '' }));
      return;
    }
    if (e.kind === 'network' || e.kind === 'conflict') onReload();
    setLine(e.kind === 'invalid' ? e.message : t(saveErrorKey(e) ?? 'promptStudio.common.saveFailed'));
  };

  async function save() {
    if (variant === 'plan') {
      const r = await conversationsApi.reactivate(brandId, persona.personaId);
      onReactivated(r.persona, r.load);
    } else {
      const r = await conversationsApi.update(brandId, persona.personaId, { status: 'active' });
      onReactivated(savedPersona(r), r.load);
    }
  }

  async function handleReactivate() {
    if (loading || limited) return;
    setLine(null);
    setLoading(true);
    try {
      const check = variant === 'plan'
        ? await conversationsApi.previewReactivate(brandId, persona.personaId)
        : await conversationsApi.previewStatus(brandId, persona.personaId, 'active');
      if (isAgencyCheck(check)) {
        if (isAgency && check.warnings.length > 0) {
          setWarnings(check.warnings);
          return;
        }
      } else if (check.refused) {
        const slot = limitReason(check.figuresAfter, 'conversationRuns');
        setLimited(slot ? slotText(t, slot) : t('promptStudio.common.saveFailed'));
        return;
      }
      await save();
    } catch (err) {
      fail(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirm() {
    setWarnings(null);
    setLoading(true);
    try {
      await save();
    } catch (err) {
      fail(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <PausedRowShell
        text={title}
        line={limited ?? line}
        disabled={loading || !!limited}
        disabledTitle={limited ?? ''}
        loading={loading}
        onReactivate={() => void handleReactivate()}
      />
      <QuotaWarningDialog
        open={warnings !== null && warnings.length > 0}
        warnings={warnings}
        onConfirm={() => void handleConfirm()}
        onCancel={() => setWarnings(null)}
      />
    </>
  );
}
